import { useEffect, useId, useState } from "react";
import { createFileRoute, Link, useBlocker, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Trash2, Star, ChevronDown, ChevronUp, ShieldCheck, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useConsents,
  CONSENT_TEXT_VERSION,
  useDebouncedValue,
  useLocationSearch,
  useLocations,
  useUserProfile,
  useUserSettings,
} from "@/hooks";
import { setAppLanguage } from "@/i18n";
import { normalizeLanguage, type AppLanguage } from "@/i18n/languages";
import { findBestLocationSuggestion } from "@/lib/location-search";
import type { BodyModel, ConsentType, DateFormat, TemperatureUnit } from "@floline/api-client";
import { ApiClientError } from "@floline/api-client";

type AiCheckInLanguageSetting = AppLanguage | "app";

function toAiCheckInLanguageSetting(value: AppLanguage | null | undefined): AiCheckInLanguageSetting {
  return value === "fi" || value === "en" ? value : "app";
}

function fromAiCheckInLanguageSetting(value: AiCheckInLanguageSetting): AppLanguage | null {
  return value === "app" ? null : value;
}

function splitName(name: string | null | undefined): { firstName: string; lastName: string } {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

/**
 * Maps ConsentType identifiers to their i18n key prefixes.
 */
const CONSENT_I18N_KEY: Record<ConsentType, string> = {
  core_health_processing: "coreHealthProcessing",
  audio_transcription: "audioTranscription",
  ai_extraction: "aiExtraction",
  ai_insights: "aiInsights",
};

/**
 * Optional consent types that can be toggled on/off in Settings.
 */
const OPTIONAL_CONSENT_TYPES: ConsentType[] = [
  "audio_transcription",
  "ai_extraction",
  "ai_insights",
];

function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("settings");
  const { t: tc } = useTranslation("common");

  // Unique id prefix for aria-describedby associations
  const idPrefix = useId();

  const {
    settings,
    updateSettings,
    isUpdating,
    updateError,
  } = useUserSettings();
  const {
    profile,
    isLoadingProfile,
    updateProfile,
    isUpdatingProfile,
    updateProfileError,
  } = useUserProfile();

  const {
    locations,
    isLoading: locationsLoading,
    createLocation,
    isCreating: locationCreating,
    createError: locationCreateError,
    updateLocation,
    deleteLocation,
    isDeleting: locationDeleting,
  } = useLocations();

  const {
    consents,
    isLoading: consentsLoading,
    updateConsents,
    isUpdating: isUpdatingConsents,
    history,
    isFetchedHistory,
    isLoadingHistory,
    fetchHistory,
  } = useConsents();

  const [bodyModel, setBodyModel] = useState<BodyModel>("male");
  const [language, setLanguage] = useState<AppLanguage>(
    normalizeLanguage(i18n.resolvedLanguage ?? i18n.language)
  );
  const [aiCheckInLanguage, setAiCheckInLanguage] = useState<AiCheckInLanguageSetting>("app");
  const [dateFormat, setDateFormat] = useState<DateFormat>("locale");
  const [temperatureUnit, setTemperatureUnit] = useState<TemperatureUnit>("c");
  const [weatherOverlayEnabled, setWeatherOverlayEnabled] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileFormError, setProfileFormError] = useState<string | null>(null);

  const [saved, setSaved] = useState(false);

  // Location search state (immediate mutations, not deferred to Save)
  const [cityQuery, setCityQuery] = useState("");
  const [locationSearchError, setLocationSearchError] = useState<string | null>(null);
  // Confirmation dialog state for location deletion
  const [deleteLocationId, setDeleteLocationId] = useState<string | null>(null);
  const debouncedCityQuery = useDebouncedValue(cityQuery, 250);
  const {
    results: citySuggestions,
    isFetching: citySuggestionsLoading,
  } = useLocationSearch(debouncedCityQuery);
  const selectedCitySuggestion = findBestLocationSuggestion(cityQuery, citySuggestions);
  const showNoCityMatches =
    debouncedCityQuery.trim().length >= 2 && !citySuggestionsLoading && citySuggestions.length === 0;
  const showCityHint = !citySuggestionsLoading && !showNoCityMatches;

  // Consent management state
  const [consentSaved, setConsentSaved] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [withdrawConfirmType, setWithdrawConfirmType] = useState<ConsentType | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const userName = splitName(profile ? `${profile.firstName} ${profile.lastName}` : "");
  const baselineFirstName = userName.firstName;
  const baselineLastName = userName.lastName;
  const baselineEmail = profile?.email ?? "";
  const emailChanged = baselineEmail.trim().toLowerCase() !== email.trim().toLowerCase();

  useEffect(() => {
    if (!settings) return;
    if (!settingsDirty) {
      setBodyModel(settings.bodyModel);
      setLanguage(settings.language);
      setAiCheckInLanguage(toAiCheckInLanguageSetting(settings.aiCheckInLanguage));
      setDateFormat(settings.dateFormat ?? "locale");
      setTemperatureUnit(settings.temperatureUnit ?? "c");
      setWeatherOverlayEnabled(settings.weatherOverlayEnabled ?? false);
    }
  }, [settings, settingsDirty]);

  useEffect(() => {
    if (!profile) return;
    if (!profileDirty) {
      setFirstName(baselineFirstName);
      setLastName(baselineLastName);
      setEmail(baselineEmail);
      setCurrentEmail("");
    }
  }, [profile, profileDirty, baselineFirstName, baselineLastName, baselineEmail]);

  const settingsHasChanges = settings
    ? bodyModel !== settings.bodyModel ||
      language !== settings.language ||
      aiCheckInLanguage !== toAiCheckInLanguageSetting(settings.aiCheckInLanguage) ||
      dateFormat !== (settings.dateFormat ?? "locale") ||
      temperatureUnit !== (settings.temperatureUnit ?? "c") ||
      weatherOverlayEnabled !== (settings.weatherOverlayEnabled ?? false)
    : settingsDirty;

  const profileHasChanges = profile
    ? firstName.trim() !== baselineFirstName ||
      lastName.trim() !== baselineLastName ||
      email.trim().toLowerCase() !== baselineEmail.trim().toLowerCase()
    : profileDirty;

  const hasChanges = settingsHasChanges || profileHasChanges;
  const isSaving = isUpdating || isUpdatingProfile;

  const blocker = useBlocker({
    shouldBlockFn: () => hasChanges && !isSaving,
    withResolver: true,
    enableBeforeUnload: hasChanges,
  });

  function handleBodyModelChange(value: string) {
    setBodyModel(value as BodyModel);
    setSettingsDirty(true);
  }

  function handleLanguageChange(value: string) {
    setLanguage(value as AppLanguage);
    setSettingsDirty(true);
  }

  function handleAiCheckInLanguageChange(value: string) {
    setAiCheckInLanguage(value as AiCheckInLanguageSetting);
    setSettingsDirty(true);
  }

  function handleTemperatureUnitChange(value: string) {
    setTemperatureUnit(value as TemperatureUnit);
    setSettingsDirty(true);
  }

  function handleDateFormatChange(value: string) {
    setDateFormat(value as DateFormat);
    setSettingsDirty(true);
  }

  function handleWeatherOverlayToggle(checked: boolean) {
    setWeatherOverlayEnabled(checked);
    setSettingsDirty(true);
  }

  async function handleSave() {
    setProfileFormError(null);

    if (profileHasChanges && emailChanged && currentEmail.trim().toLowerCase() !== baselineEmail.trim().toLowerCase()) {
      setProfileFormError(t("profile.currentEmailMismatch"));
      return false;
    }

    try {
      if (profileHasChanges) {
        await updateProfile({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim().toLowerCase(),
          ...(emailChanged ? { currentEmail: currentEmail.trim().toLowerCase() } : {}),
        });
      }

      if (settingsHasChanges) {
        await updateSettings({
          bodyModel,
          language,
          aiCheckInLanguage: fromAiCheckInLanguageSetting(aiCheckInLanguage),
          dateFormat,
          temperatureUnit,
          weatherOverlayEnabled,
        });
        await setAppLanguage(language);
      }

      setSettingsDirty(false);
      setProfileDirty(false);
      setCurrentEmail("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      return true;
    } catch (err: unknown) {
      if (err instanceof ApiClientError) {
        if (err.message.includes("Current email")) {
          setProfileFormError(t("profile.currentEmailMismatch"));
        } else if (err.message.includes("already in use")) {
          setProfileFormError(t("profile.emailInUse"));
        }
      }
      return false;
    }
  }

  async function handleSaveAndLeave() {
    const ok = await handleSave();
    if (!ok) {
      blocker.reset?.();
      return;
    }
    blocker.proceed?.();
  }

  function handleDiscardAndLeave() {
    setSettingsDirty(false);
    setProfileDirty(false);
    setProfileFormError(null);

    if (settings) {
      setBodyModel(settings.bodyModel);
      setLanguage(settings.language);
      setAiCheckInLanguage(toAiCheckInLanguageSetting(settings.aiCheckInLanguage));
      setDateFormat(settings.dateFormat ?? "locale");
      setTemperatureUnit(settings.temperatureUnit ?? "c");
      setWeatherOverlayEnabled(settings.weatherOverlayEnabled ?? false);
    }

    if (profile) {
      setFirstName(baselineFirstName);
      setLastName(baselineLastName);
      setEmail(baselineEmail);
      setCurrentEmail("");
    }

    blocker.proceed?.();
  }

  async function handleLocationSearch() {
    if (!selectedCitySuggestion) {
      setLocationSearchError(t("weather.selectionRequired"));
      return;
    }

    setLocationSearchError(null);
    try {
      await createLocation({ query: selectedCitySuggestion.name });
      setCityQuery("");
    } catch (err: unknown) {
      if (err instanceof ApiClientError && err.status === 429) {
        setLocationSearchError(t("weather.rateLimitNote"));
      } else {
        setLocationSearchError(t("weather.searchError"));
      }
    }
  }

  async function handleSetDefault(id: string) {
    await updateLocation({ id, data: { isDefault: true } });
  }

  async function handleDeleteLocation(id: string) {
    setDeleteLocationId(id);
  }

  async function handleConfirmDelete() {
    if (!deleteLocationId) return;
    await deleteLocation(deleteLocationId);
    setDeleteLocationId(null);
  }

  // --- Consent handlers ---

  /** Helper: is a given consent type currently granted? */
  function isConsentGranted(type: ConsentType): boolean {
    const entry = consents.find((c) => c.consentType === type);
    return entry?.given ?? false;
  }

  /** Grant a single optional consent */
  async function handleGrantConsent(type: ConsentType) {
    setConsentError(null);
    const i18nKey = CONSENT_I18N_KEY[type];
    try {
      await updateConsents({
        decisions: {
          [type]: {
            given: true,
            consentTextVersion: CONSENT_TEXT_VERSION,
            consentText: t(`consent.${i18nKey}.text`),
          },
        },
      });
      setConsentSaved(true);
      setTimeout(() => setConsentSaved(false), 2000);
    } catch {
      setConsentError(t("consent.saveError"));
    }
  }

  /** Withdraw a single optional consent (after confirmation) */
  async function handleConfirmWithdraw() {
    if (!withdrawConfirmType) return;
    setConsentError(null);
    const type = withdrawConfirmType;
    const i18nKey = CONSENT_I18N_KEY[type];
    try {
      await updateConsents({
        decisions: {
          [type]: {
            given: false,
            consentTextVersion: CONSENT_TEXT_VERSION,
            consentText: t(`consent.${i18nKey}.text`),
          },
        },
      });
      setWithdrawConfirmType(null);
      setConsentSaved(true);
      setTimeout(() => setConsentSaved(false), 2000);
    } catch {
      setConsentError(t("consent.saveError"));
      setWithdrawConfirmType(null);
    }
  }

  function handleToggleHistory() {
    if (!showHistory && !isFetchedHistory) {
      void fetchHistory();
    }
    setShowHistory((v) => !v);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: "/" })}
          aria-label={tc("actions.back")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">{t("title")}</h1>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t("profile.title")}</CardTitle>
          <CardDescription>
            {t("profile.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="first-name" className="text-sm font-medium">
              {t("profile.firstName")}
            </label>
            <Input
              id="first-name"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setProfileDirty(true);
                setProfileFormError(null);
              }}
              disabled={isSaving || isLoadingProfile}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="last-name" className="text-sm font-medium">
              {t("profile.lastName")}
            </label>
            <Input
              id="last-name"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setProfileDirty(true);
                setProfileFormError(null);
              }}
              disabled={isSaving || isLoadingProfile}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              {t("profile.email")}
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={profileFormError ? true : undefined}
              aria-describedby={profileFormError ? `${idPrefix}-profile-error` : undefined}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setProfileDirty(true);
                setProfileFormError(null);
              }}
              disabled={isSaving || isLoadingProfile}
            />
          </div>

          {emailChanged && (
            <div className="space-y-2">
              <label htmlFor="current-email" className="text-sm font-medium">
                {t("profile.currentEmail")}
              </label>
              <Input
                id="current-email"
                type="email"
                autoComplete="email"
                aria-invalid={profileFormError ? true : undefined}
                aria-describedby={[
                  `${idPrefix}-current-email-hint`,
                  profileFormError ? `${idPrefix}-profile-error` : null,
                ].filter(Boolean).join(" ") || undefined}
                value={currentEmail}
                onChange={(e) => {
                  setCurrentEmail(e.target.value);
                  setProfileFormError(null);
                }}
                placeholder={baselineEmail}
                disabled={isSaving}
              />
              <p id={`${idPrefix}-current-email-hint`} className="text-xs text-muted-foreground">{t("profile.currentEmailHint")}</p>
            </div>
          )}

          {profileFormError && (
            <p id={`${idPrefix}-profile-error`} className="text-sm text-destructive" role="alert">
              {profileFormError}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t("bodyDiagram.title")}</CardTitle>
          <CardDescription>
            {t("bodyDiagram.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="body-model" className="text-sm font-medium">
              {t("bodyDiagram.selectionLabel")}
            </label>
            <Select
              value={bodyModel}
              onValueChange={handleBodyModelChange}
              disabled={isUpdating}
            >
              <SelectTrigger id="body-model">
                <SelectValue placeholder={t("bodyDiagram.placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t("bodyDiagram.masculine")}</SelectItem>
                <SelectItem value="female">{t("bodyDiagram.feminine")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t("language.title")}</CardTitle>
          <CardDescription>
            {t("language.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="language" className="text-sm font-medium">
              {t("language.label")}
            </label>
            <Select
              value={language}
              onValueChange={handleLanguageChange}
              disabled={isUpdating}
            >
              <SelectTrigger id="language">
                <SelectValue placeholder={t("language.placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{t("language.english")}</SelectItem>
                <SelectItem value="fi">{t("language.finnish")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t("aiCheckInLanguage.title")}</CardTitle>
          <CardDescription>
            {t("aiCheckInLanguage.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="ai-check-in-language" className="text-sm font-medium">
              {t("aiCheckInLanguage.label")}
            </label>
            <Select
              value={aiCheckInLanguage}
              onValueChange={handleAiCheckInLanguageChange}
              disabled={isUpdating}
            >
              <SelectTrigger id="ai-check-in-language">
                <SelectValue placeholder={t("aiCheckInLanguage.placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="app">{t("aiCheckInLanguage.appDefault")}</SelectItem>
                <SelectItem value="en">{t("aiCheckInLanguage.english")}</SelectItem>
                <SelectItem value="fi">{t("aiCheckInLanguage.finnish")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t("dateFormat.title")}</CardTitle>
          <CardDescription>{t("dateFormat.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="date-format" className="text-sm font-medium">
              {t("dateFormat.label")}
            </label>
            <Select value={dateFormat} onValueChange={handleDateFormatChange} disabled={isUpdating}>
              <SelectTrigger id="date-format">
                <SelectValue placeholder={t("dateFormat.placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="locale">{t("dateFormat.locale")}</SelectItem>
                <SelectItem value="dmy">{t("dateFormat.dmy")}</SelectItem>
                <SelectItem value="mdy">{t("dateFormat.mdy")}</SelectItem>
                <SelectItem value="ymd">{t("dateFormat.ymd")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t("weather.title")}</CardTitle>
          <CardDescription>
            {t("weather.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="city-search" className="text-sm font-medium">
              {t("weather.locationLabel")}
            </label>
            <div className="flex gap-2">
              <Input
                id="city-search"
                list="settings-city-suggestions"
                value={cityQuery}
                onChange={(e) => {
                  setCityQuery(e.target.value);
                  setLocationSearchError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleLocationSearch();
                  }
                }}
                placeholder={t("weather.locationPlaceholder")}
                disabled={locationCreating}
                aria-invalid={locationSearchError ? true : undefined}
                aria-describedby={[
                  showCityHint ? `${idPrefix}-city-hint` : null,
                  locationSearchError ? `${idPrefix}-location-error` : null,
                ].filter(Boolean).join(" ") || undefined}
              />
                <Button
                  variant="secondary"
                  onClick={handleLocationSearch}
                  disabled={locationCreating || !selectedCitySuggestion}
                >
                  {t("weather.searchButton")}
                </Button>
              </div>
              <datalist id="settings-city-suggestions">
                {citySuggestions.map((city) => (
                  <option key={`${city.id}:${city.latitude}:${city.longitude}`} value={city.label} />
                ))}
              </datalist>
              {citySuggestionsLoading && debouncedCityQuery.trim().length >= 2 && (
                <p className="text-xs text-muted-foreground" aria-live="polite">{t("weather.searching")}</p>
              )}
              {showCityHint && (
                <p id={`${idPrefix}-city-hint`} className="text-xs text-muted-foreground">{t("weather.searchHint")}</p>
              )}
              {showNoCityMatches && (
                <p className="text-xs text-muted-foreground" aria-live="polite">{t("weather.noMatches")}</p>
              )}
              {locationSearchError && (
                <p id={`${idPrefix}-location-error`} className="text-sm text-destructive" role="alert">
                  {locationSearchError}
                </p>
              )}
            {locationCreateError && !locationSearchError && (
              <p className="text-sm text-destructive" role="alert">{t("weather.searchError")}</p>
            )}
          </div>

          {!locationsLoading && locations.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("weather.noLocations")}</p>
          )}

          {locations.length > 0 && (
            <ul className="space-y-2">
              {locations.map((loc) => (
                <li
                  key={loc.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate font-medium">{loc.label}</span>
                    {loc.isDefault && (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {t("weather.defaultBadge")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!loc.isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(loc.id)}
                        title={t("weather.setDefault")}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLocation(loc.id)}
                      disabled={locationDeleting}
                      title={t("weather.deleteLocation")}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-2">
            <label htmlFor="temp-unit" className="text-sm font-medium">
              {t("weather.temperatureUnitLabel")}
            </label>
            <Select
              value={temperatureUnit}
              onValueChange={handleTemperatureUnitChange}
              disabled={isUpdating}
            >
              <SelectTrigger id="temp-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="c">{t("weather.celsius")}</SelectItem>
                <SelectItem value="f">{t("weather.fahrenheit")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <label htmlFor="weather-overlay" className="text-sm font-medium">
              {t("weather.overlayToggle")}
            </label>
            <Switch
              id="weather-overlay"
              checked={weatherOverlayEnabled}
              onCheckedChange={handleWeatherOverlayToggle}
              disabled={isUpdating}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {t("weather.privacyNote")}
          </p>
        </CardContent>
      </Card>

      {/* Consent Management Card */}
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t("consent.title")}</CardTitle>
          <CardDescription>{t("consent.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {consentsLoading && (
            <p className="text-sm text-muted-foreground">Loading consent state...</p>
          )}

          {/* Core health processing — required, non-togglable */}
          <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
              <p className="text-sm font-medium">{t("consent.coreHealthProcessing.label")}</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("consent.coreHealthProcessing.text")}
            </p>
            <p className="text-xs font-medium text-primary">
              {t("consent.coreHealthProcessing.status")}
            </p>
          </div>

          {/* Optional consents — can be granted/withdrawn */}
          {OPTIONAL_CONSENT_TYPES.map((type) => {
            const i18nKey = CONSENT_I18N_KEY[type];
            const granted = isConsentGranted(type);

            return (
              <div key={type} className="rounded-md border p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {granted ? (
                        <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                      ) : (
                        <ShieldX className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <p className="text-sm font-medium">{t(`consent.${i18nKey}.label`)}</p>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t(`consent.${i18nKey}.text`)}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      granted
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {granted ? t("consent.granted") : t("consent.withdrawn")}
                  </span>
                </div>

                {/* Consequence warning when granted (shown to inform before withdrawal) */}
                {granted && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                    {t(`consent.${i18nKey}.withdrawWarning`)}
                  </p>
                )}

                <div>
                  {granted ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setWithdrawConfirmType(type)}
                      disabled={isUpdatingConsents}
                    >
                      {t("consent.withdrawConfirmAction")}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void handleGrantConsent(type)}
                      disabled={isUpdatingConsents}
                    >
                      {t("consent.grantAction")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {consentError && <p className="text-sm text-destructive" role="alert">{consentError}</p>}
          {consentSaved && (
            <p className="text-sm text-green-700 dark:text-green-400" aria-live="polite" aria-atomic="true">
              {t("consent.saved")}
            </p>
          )}

          {/* Link to full legal pages */}
          <p className="text-xs text-muted-foreground">
            <Link to="/privacy" className="font-medium text-primary underline-offset-4 hover:underline">
              {tc("legal.privacyPolicy")}
            </Link>
            {" · "}
            <Link to="/terms" className="font-medium text-primary underline-offset-4 hover:underline">
              {tc("legal.termsOfService")}
            </Link>
            {" · "}
            <Link to="/data-access" className="font-medium text-primary underline-offset-4 hover:underline">
              View my data
            </Link>
          </p>

          {/* Consent history toggle */}
          <div className="pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleHistory}
              className="gap-1.5"
            >
              {showHistory ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {showHistory ? t("consent.hideHistory") : t("consent.showHistory")}
            </Button>

            {showHistory && (
              <div className="mt-3 space-y-2">
                <h4 className="text-sm font-medium">{t("consent.historyTitle")}</h4>
                {isLoadingHistory && (
                  <p className="text-xs text-muted-foreground">Loading...</p>
                )}
                {isFetchedHistory && history.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t("consent.historyEmpty")}</p>
                )}
                {history.length > 0 && (
                  <ul className="space-y-2 max-h-64 overflow-y-auto">
                    {history.map((event) => {
                      const eventI18nKey = CONSENT_I18N_KEY[event.consentType] ?? event.consentType;
                      return (
                        <li
                          key={event.id}
                          className="rounded-md border px-3 py-2 text-xs space-y-0.5"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">
                              {t(`consent.${eventI18nKey}.label`)}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                event.action === "give"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                              }`}
                            >
                              {t(`consent.historyAction_${event.action}`)}
                            </span>
                          </div>
                          <p className="text-muted-foreground">
                            {new Date(event.createdAt).toLocaleString()}
                            {" · "}
                            {t("consent.historyVersion", { version: event.consentTextVersion })}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {(updateError || updateProfileError) && !profileFormError && (
        <p className="text-sm text-destructive" role="alert">
          {t("saveFailed")}
        </p>
      )}

      {saved && (
        <p className="text-sm text-green-700" aria-live="polite" aria-atomic="true">
          {t("saved")}
        </p>
      )}

      <div>
        <Button onClick={() => void handleSave()} disabled={isSaving || !hasChanges}>
          {isSaving ? tc("actions.saving") : t("saveSettings")}
        </Button>
      </div>

      {/* Withdraw consent confirmation dialog */}
      <Dialog
        open={withdrawConfirmType !== null}
        onOpenChange={(open) => {
          if (!open) setWithdrawConfirmType(null);
        }}
      >
        <DialogContent aria-describedby="withdraw-consent-description">
          <DialogHeader>
            <DialogTitle>{t("consent.withdrawConfirmTitle")}</DialogTitle>
            <DialogDescription id="withdraw-consent-description">
              {withdrawConfirmType &&
                t("consent.withdrawConfirmDescription", {
                  label: t(`consent.${CONSENT_I18N_KEY[withdrawConfirmType]}.label`),
                })}
            </DialogDescription>
          </DialogHeader>
          {withdrawConfirmType && (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              {t(`consent.${CONSENT_I18N_KEY[withdrawConfirmType]}.withdrawWarning`)}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWithdrawConfirmType(null)}
            >
              {tc("actions.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmWithdraw()}
              disabled={isUpdatingConsents}
            >
              {t("consent.withdrawConfirmAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location delete confirmation dialog */}
      <Dialog
        open={deleteLocationId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteLocationId(null);
        }}
      >
        <DialogContent aria-describedby="delete-location-description">
          <DialogHeader>
            <DialogTitle>{t("weather.deleteConfirmTitle")}</DialogTitle>
            <DialogDescription id="delete-location-description">
              {t("weather.deleteConfirmation")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteLocationId(null)}
            >
              {tc("actions.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={locationDeleting}
            >
              {t("weather.deleteLocation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes dialog */}
      <Dialog
        open={blocker.status === "blocked"}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <DialogContent aria-describedby="unsaved-settings-description">
          <DialogHeader>
            <DialogTitle>{t("unsavedDialog.title")}</DialogTitle>
            <DialogDescription id="unsaved-settings-description">
              {t("unsavedDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => blocker.reset?.()}>
              {t("unsavedDialog.stay")}
            </Button>
            <Button variant="secondary" onClick={handleDiscardAndLeave}>
              {t("unsavedDialog.discardAndLeave")}
            </Button>
            <Button onClick={() => void handleSaveAndLeave()} disabled={isSaving}>
              {t("unsavedDialog.saveAndLeave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});
